// Final Settlement (terminal dues) — compute a leaver's final pay: prorated final
// salary, accrued-leave payout, severance, and payment in lieu of notice. Talks to
// payroll-service via terminalDuesService. The numeric policy is per legal entity
// (configurable in Payroll Settings); this page just drives the calculation.
// Recorded settlements can be paid out from the history table: prepare builds a
// single-item disbursement batch; release is DUAL-CONTROLLED (a different
// approver than the preparer must press Release).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { UserMinus, Loader2, Calculator, Printer, History, Send, CheckCircle2, AlertTriangle, RotateCcw, SearchX } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/features/shared/components/ui/table';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import {
  listPayrollEmployees,
  computeTerminalDues,
  listTerminalSettlements,
  type PayrollEmployeeLite,
  type Settlement,
  type ExitType,
} from '@/services/terminalDuesService';
import { payrollApi } from '../api/payrollApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { StatusChip } from '../components/StatusChip';
import { DISBURSEMENT_BATCH_META } from '../lib/state';
import { formatDate } from '../lib/format';
import type { DisbursementBatch } from '../types';

const EXIT_TYPES: { value: ExitType; label: string }[] = [
  { value: 'termination', label: 'Termination (employer)' },
  { value: 'redundancy', label: 'Redundancy / retrenchment' },
  { value: 'resignation', label: 'Resignation (employee)' },
  { value: 'retirement', label: 'Retirement' },
  { value: 'death', label: 'Death in service' },
];

const fieldCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

/** Where the previous-settlements load stands. A failed load is NEVER shown as
 *  "no previous settlements" — that would claim something about the person. */
export type SettlementHistory =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

/** 404 = the employee is not found for this viewer (unknown, or outside the
 *  companies they can access — the server answers the two the same); anything
 *  else is an error carrying the server's own words. */
export function settlementHistoryFailure(e: unknown): SettlementHistory {
  const status = (e as { response?: { status?: number } } | null)?.response?.status;
  if (status === 404) return { kind: 'not_found' };
  return { kind: 'error', message: getApiErrorMessage(e, 'Could not load previous settlements.') };
}

function money(amount: string, currency: string): string {
  const n = Number(amount);
  if (Number.isNaN(n)) return `${currency} ${amount}`;
  return `${currency} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export const FinalSettlementPage: React.FC = () => {
  const { t } = useTranslation('payroll');
  const confirm = useConfirm();
  const { canApprovePayroll } = useRoleAccess();
  const [employees, setEmployees] = useState<PayrollEmployeeLite[]>([]);
  const [employeeId, setEmployeeId] = useState('');
  const [exitType, setExitType] = useState<ExitType>('termination');
  const [exitDate, setExitDate] = useState('');
  const [leaveDays, setLeaveDays] = useState('');
  const [result, setResult] = useState<Settlement | null>(null);
  const [past, setPast] = useState<Settlement[]>([]);
  const [history, setHistory] = useState<SettlementHistory>({ kind: 'idle' });
  // The employee whose history the page is showing — a slow answer for a
  // previously selected person must not land on the current one.
  const historyFor = useRef('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Payout batches prepared THIS session, keyed by settlement id. Never fetched
  // on render — disburseSettlement CREATES a batch, so it only runs on an
  // explicit user action; the terminal "paid out" display relies on disbursed_at.
  const [payoutBatches, setPayoutBatches] = useState<Record<string, DisbursementBatch>>({});
  const [payoutBusy, setPayoutBusy] = useState<string | null>(null);

  useEffect(() => {
    listPayrollEmployees().then(setEmployees).catch(() => setEmployees([]));
  }, []);

  const loadHistory = useCallback((id: string) => {
    historyFor.current = id;
    if (!id) {
      setPast([]);
      setHistory({ kind: 'idle' });
      return;
    }
    setHistory({ kind: 'loading' });
    listTerminalSettlements(id)
      .then((list) => {
        if (historyFor.current !== id) return;
        setPast(list);
        setHistory({ kind: 'ready' });
      })
      .catch((e) => {
        if (historyFor.current !== id) return;
        setPast([]);
        setHistory(settlementHistoryFailure(e));
      });
  }, []);

  useEffect(() => {
    loadHistory(employeeId);
  }, [employeeId, loadHistory]);

  const selectedName = useMemo(
    () => employees.find((e) => e.employee_id === employeeId)?.full_name ?? '',
    [employees, employeeId],
  );

  const compute = async () => {
    setError(null);
    if (!employeeId || !exitDate) {
      setError('Pick an employee and the last working day.');
      return;
    }
    setBusy(true);
    try {
      const s = await computeTerminalDues(employeeId, {
        exit_type: exitType,
        exit_date: exitDate,
        leave_balance_days: leaveDays || '0',
      });
      setResult(s);
      loadHistory(employeeId);
    } catch (e: any) {
      setError(getApiErrorMessage(e, 'Could not compute the settlement.'));
    } finally {
      setBusy(false);
    }
  };

  /** Prepare the single-item payout batch for a recorded settlement (idempotent
   *  on the backend). A DIFFERENT approver must then release it. */
  const preparePayout = async (s: Settlement) => {
    const ok = await confirm({
      title: t('finalSettlement.preparePayoutTitle', { defaultValue: 'Prepare payout?' }),
      message: t('finalSettlement.preparePayoutMessage', {
        defaultValue:
          'This prepares a payout of {{amount}} to {{name}}. Dual control applies: a DIFFERENT approver must release the payment — you will not be able to release it yourself.',
        amount: money(s.gross, s.currency),
        name: selectedName || t('finalSettlement.theEmployee', { defaultValue: 'the employee' }),
      }),
      confirmLabel: t('finalSettlement.preparePayoutConfirm', { defaultValue: 'Prepare payout' }),
    });
    if (!ok) return;
    setPayoutBusy(s.id);
    try {
      const batch = await payrollApi.disburseSettlement(s.id);
      setPayoutBatches((prev) => ({ ...prev, [s.id]: batch }));
      toast.success(
        t('finalSettlement.payoutPrepared', {
          defaultValue: 'Payout batch prepared — a second approver must release it from this page',
        }),
        { duration: 6000 },
      );
    } catch (e: any) {
      const data = e?.response?.data;
      if (e?.response?.status === 422 && data?.code === 'missing_payment_profiles') {
        toast.error(
          getApiErrorMessage(e, t('finalSettlement.missingProfiles', {
            defaultValue: 'The employee has no payment profile — add bank/wallet details first.',
          })),
          { duration: 8000 },
        );
      } else {
        toast.error(
          getApiErrorMessage(e, t('finalSettlement.preparePayoutError', { defaultValue: 'Could not prepare the payout' })),
          { duration: 6000 },
        );
      }
    } finally {
      setPayoutBusy(null);
    }
  };

  /** Release a prepared payout batch (existing batch submit — dual control:
   *  the preparer gets a 409 segregation_of_duties, surfaced non-destructively). */
  const releasePayout = async (s: Settlement) => {
    const batch = payoutBatches[s.id];
    if (!batch) return;
    setPayoutBusy(s.id);
    try {
      const released = await payrollApi.releaseBatch(batch.id);
      setPayoutBatches((prev) => ({ ...prev, [s.id]: released }));
      toast.success(t('finalSettlement.payoutReleased', { defaultValue: 'Payout released' }));
      // The settlement's disbursed_at is stamped once the batch settles all-paid.
      loadHistory(employeeId);
    } catch (e: any) {
      const data = e?.response?.data;
      if (e?.response?.status === 409 && data?.code === 'segregation_of_duties') {
        toast.error(
          t('finalSettlement.segregationOfDuties', {
            defaultValue: 'You prepared this payout — a different approver must release it',
          }),
          { duration: 8000 },
        );
      } else {
        toast.error(
          getApiErrorMessage(e, t('finalSettlement.releaseError', { defaultValue: 'Could not release the payout' })),
          { duration: 6000 },
        );
      }
    } finally {
      setPayoutBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-primary-500/10 p-2 text-primary-500">
          <UserMinus className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Final Settlement</h1>
          <p className="text-sm text-muted-foreground">
            Terminal dues for a leaver — final salary, leave payout, severance and notice pay.
          </p>
        </div>
      </div>

      {/* Calculator */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className="space-y-1.5 sm:col-span-2">
              <span className="text-xs font-medium text-muted-foreground">Employee</span>
              <select className={fieldCls} value={employeeId} onChange={(e) => { setEmployeeId(e.target.value); setResult(null); }}>
                <option value="">Select an employee…</option>
                {employees.map((e) => (
                  <option key={e.employee_id} value={e.employee_id}>{e.full_name}</option>
                ))}
              </select>
            </label>
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Reason for exit</span>
              <select className={fieldCls} value={exitType} onChange={(e) => setExitType(e.target.value as ExitType)}>
                {EXIT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Last working day</span>
              <input type="date" className={fieldCls} value={exitDate} onChange={(e) => setExitDate(e.target.value)} />
            </label>
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Accrued leave (days)</span>
              <input type="number" min="0" step="0.5" className={fieldCls} value={leaveDays} placeholder="0" onChange={(e) => setLeaveDays(e.target.value)} />
            </label>
          </div>

          {error && <p className="text-sm text-danger">{error}</p>}

          <div className="flex items-center gap-3">
            <Button onClick={compute} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Calculator className="mr-2 h-4 w-4" />}
              Compute settlement
            </Button>
            <p className="text-xs text-muted-foreground">
              Severance & notice apply to employer-initiated exits; resignation pays only final salary + leave.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Result */}
      {result && (
        <Card className="print:shadow-none">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Settlement statement</h2>
                <p className="text-sm text-muted-foreground">
                  {selectedName} · {EXIT_TYPES.find((t) => t.value === result.exit_type)?.label} · last day {result.exit_date}
                  {result.years_of_service > 0 && ` · ${result.years_of_service} yr${result.years_of_service > 1 ? 's' : ''} of service`}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => window.print()} className="print:hidden">
                <Printer className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Component</TableHead>
                  <TableHead>Basis</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.lines.map((l) => (
                  <TableRow key={l.code}>
                    <TableCell className="font-medium">{l.label}</TableCell>
                    <TableCell className="text-muted-foreground">{l.detail}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(l.amount, result.currency)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell className="font-semibold">Total gross settlement</TableCell>
                  <TableCell />
                  <TableCell className="text-right font-semibold tabular-nums">{money(result.gross, result.currency)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <p className="text-xs text-muted-foreground">
              Gross terminal dues. Statutory tax treatment of terminal benefits (PAYE on the taxable
              portions) is applied when these are included in a final payroll run.
            </p>
          </CardContent>
        </Card>
      )}

      {/* History load state — a failure is never rendered as "no settlements". */}
      {history.kind === 'loading' && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading previous settlements…
        </p>
      )}
      {history.kind === 'not_found' && (
        <Card>
          <CardContent className="flex items-start gap-3 p-5" role="alert">
            <SearchX className="mt-0.5 h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-foreground">Not found</p>
              <p className="text-sm text-muted-foreground">
                This employee was not found in payroll for the companies you can access.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {history.kind === 'error' && (
        <Card>
          <CardContent className="flex items-start justify-between gap-3 p-5" role="alert">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-danger" />
              <div>
                <p className="text-sm font-medium text-foreground">Couldn't load previous settlements</p>
                <p className="text-sm text-muted-foreground">{history.message}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => loadHistory(employeeId)}>
              <RotateCcw className="mr-2 h-4 w-4" /> Try again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* History */}
      {history.kind === 'ready' && past.length > 0 && (
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <History className="h-4 w-4" /> Previous settlements for {selectedName}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Computed</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Last day</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead>{t('finalSettlement.colPayout', { defaultValue: 'Payout' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {past.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="text-muted-foreground">{(s.created_at || '').slice(0, 10)}</TableCell>
                    <TableCell>{EXIT_TYPES.find((x) => x.value === s.exit_type)?.label ?? s.exit_type}</TableCell>
                    <TableCell>{s.exit_date}</TableCell>
                    <TableCell>{s.years_of_service} yr{s.years_of_service === 1 ? '' : 's'}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(s.gross, s.currency)}</TableCell>
                    <TableCell>
                      <SettlementPayoutCell
                        settlement={s}
                        batch={payoutBatches[s.id]}
                        canApprove={canApprovePayroll}
                        busy={payoutBusy === s.id}
                        onPrepare={() => preparePayout(s)}
                        onRelease={() => releasePayout(s)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/** Payout state for one settlement row: a terminal "Paid out" chip once
 *  disbursed_at is stamped; otherwise the prepared batch (status + Release), or
 *  a Prepare button for approvers. */
const SettlementPayoutCell: React.FC<{
  settlement: Settlement;
  batch?: DisbursementBatch;
  canApprove: boolean;
  busy: boolean;
  onPrepare: () => void;
  onRelease: () => void;
}> = ({ settlement, batch, canApprove, busy, onPrepare, onRelease }) => {
  const { t } = useTranslation('payroll');

  if (settlement.disbursed_at) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#DCFCE7] px-2.5 py-1 text-xs font-medium text-[#166534]">
        <CheckCircle2 className="h-3.5 w-3.5" />
        {t('finalSettlement.paidOut', {
          defaultValue: 'Paid out {{date}}',
          date: formatDate(settlement.disbursed_at),
        })}
      </span>
    );
  }

  if (batch) {
    return (
      <span className="inline-flex items-center gap-2">
        <StatusChip meta={DISBURSEMENT_BATCH_META[batch.status]} />
        {batch.status === 'prepared' && (
          <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={onRelease}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {t('finalSettlement.release', { defaultValue: 'Release' })}
          </Button>
        )}
      </span>
    );
  }

  if (canApprove) {
    return (
      <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={onPrepare}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        {t('finalSettlement.preparePayout', { defaultValue: 'Prepare payout' })}
      </Button>
    );
  }

  return <span className="text-xs text-muted-foreground">—</span>;
};

export default FinalSettlementPage;
